import { useEffect, useState } from "react";
import { Loader2, RotateCw, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { publicApi } from "@/services/api-client";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import {
  METHOD_LABEL,
  OP_LABEL,
  countryOf,
  fmt,
  fmtDate,
  signed,
  type Tx,
  type TxStatus,
} from "@/types/dashboard";
import {
  ErrorBox,
  KV,
  Tag,
  fmtAmount,
  fmtWhen,
  normalizeAmount,
  pick,
  record,
  rows,
  text,
  useFinanceGate,
  useServer,
} from "@/features/finance/kit";
import { OperationLookup } from "@/features/finance/operation-lookup";
import { Flag, StatusPill } from "./bits";
import { useI18n } from "@/i18n/i18n-context";

export function TxDetailDialog({
  tx,
  onClose,
}: {
  tx: Tx | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[560px]">
        {tx && <Body key={tx.reference} tx={tx} />}
      </DialogContent>
    </Dialog>
  );
}

function Body({ tx }: { tx: Tx }) {
  const { lang } = useI18n();
  const gate = useFinanceGate();
  const detail = useServer<Record<string, unknown>>(
    gate.allowed && tx.reference
      ? `/transactions/${encodeURIComponent(tx.reference)}`
      : null,
  );
  const full = record(record(detail.data).transaction ?? detail.data);
  const attempts = rows(
    pick(
      detail.data,
      "attempts",
      "provider_attempts",
      "transaction.attempts",
      "transaction.provider_attempts",
    ),
  );
  const timeline = rows(
    pick(detail.data, "timeline", "events", "transaction.timeline"),
  );
  const fees = pick(detail.data, "fees", "transaction.fees");
  const failure = pick(
    detail.data,
    "failure",
    "failure_reason",
    "error",
    "transaction.failure",
  );
  const rawStatus = (text(full, "status") || tx.status).toUpperCase();
  const displayedStatus: TxStatus = [
    "COMPLETED",
    "PENDING",
    "PROCESSING",
    "FAILED",
  ].includes(rawStatus)
    ? (rawStatus as TxStatus)
    : tx.status;
  const statusIsFinal = ["COMPLETED", "FAILED", "UNKNOWN"].includes(rawStatus);
  const [refundOpen, setRefundOpen] = useState(false);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{OP_LABEL[tx.operation]}</DialogTitle>
        <DialogDescription className="font-mono text-xs">
          {tx.reference}
        </DialogDescription>
      </DialogHeader>
      <div className="rounded-lg bg-accent/60 p-4 text-center">
        <p
          className={`tnum text-2xl font-extrabold ${signed(tx) < 0 ? "text-foreground" : "text-emerald-700"}`}
        >
          {signed(tx) < 0 ? "−" : "+"}
          {fmt(tx.amount, tx.currency)}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <StatusPill status={displayedStatus} />
          {text(full, "environment") && (
            <Tag value={text(full, "environment")} />
          )}
          {!statusIsFinal && gate.allowed && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void detail.reload()}
              disabled={detail.loading}
              data-testid="button-check-transaction-status"
              className="gap-1.5"
            >
              <RotateCw
                className={`h-3.5 w-3.5 ${detail.loading ? "animate-spin" : ""}`}
              />
              {lang === "en" ? "Check status" : "Vérifier le statut"}
            </Button>
          )}
        </div>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-sm">
        {[
          [
            "Wallet",
            <span key="w" className="flex items-center justify-end gap-2">
              <Flag code={tx.country} />
              {countryOf(tx.country).name} · {tx.currency}
            </span>,
          ],
          [
            "Méthode de paiement",
            String(
              METHOD_LABEL[tx.method] ??
                text(full, "payment_method") ??
                tx.method ??
                "Mobile Money",
            ).toUpperCase(),
          ],
          [
            "Numéro de téléphone",
            text(full, "phone_number") || tx.counterparty || "—",
          ],
          ["Référence fournisseur", text(full, "provider_reference") || "—"],
          ["Libellé", tx.description || "—"],
          ["Frais", fmt(tx.fee, tx.currency)],
          ["Date", tx.createdAt ? fmtDate(tx.createdAt) : "—"],
        ].map(([k, v], i) => (
          <div key={i} className="contents">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="text-right font-medium">{v}</dd>
          </div>
        ))}
      </dl>

      {!gate.allowed ? (
        <p className="text-[11px] text-muted-foreground">
          Détail complet disponible après validation du compte.
        </p>
      ) : detail.loading ? (
        <div
          className="h-24 animate-pulse rounded-lg bg-muted/70"
          aria-busy="true"
        />
      ) : detail.error ? (
        <ErrorBox error={detail.error} onRetry={detail.reload} />
      ) : (
        <div className="space-y-4" data-testid="tx-full-detail">
          {failure !== undefined && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              <p className="font-bold">Échec signalé</p>
              {typeof failure === "object" ? (
                <KV data={failure} />
              ) : (
                <p>{String(failure)}</p>
              )}
            </div>
          )}
          {fees !== undefined && (
            <section>
              <h4 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Frais
              </h4>
              {Array.isArray(fees) ? (
                <ul className="divide-y rounded-lg border text-sm">
                  {rows(fees).map((f, i) => (
                    <li key={i} className="flex justify-between px-3 py-2">
                      <span>{text(f, "label", "type", "name") || "Frais"}</span>
                      <span className="tnum font-semibold">
                        {fmtAmount(
                          pick(f, "amount"),
                          pick(f, "currency") ?? tx.currency,
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : typeof fees === "object" ? (
                <KV data={fees} />
              ) : (
                <p className="text-sm">{fmtAmount(fees, tx.currency)}</p>
              )}
            </section>
          )}
          {displayedStatus.toUpperCase() === "COMPLETED" && tx.walletId && (
            <div className="border-t pt-3">
              {refundOpen ? (
                <RefundForm tx={tx} onCancel={() => setRefundOpen(false)} />
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRefundOpen(true)}
                  data-testid="button-open-refund"
                >
                  <Undo2 className="mr-1.5 h-4 w-4" /> Initier un remboursement
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function RefundForm({ tx, onCancel }: { tx: Tx; onCancel: () => void }) {
  const { lang } = useI18n();
  const idem = useIdempotencyKey();
  const [amount, setAmount] = useState(String(tx.amount));
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const value = normalizeAmount(amount);
  const valid = Boolean(
    value && reason.trim().length >= 3 && Number(value) <= tx.amount,
  );
  useEffect(() => setConfirming(false), [amount, reason]);

  async function submit() {
    if (!value) return;
    const body = {
      transaction_reference: tx.reference,
      wallet_id: tx.walletId,
      amount: value,
      reason: reason.trim(),
    };
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<Record<string, unknown>>("/refunds", {
        method: "POST",
        headers: { "Idempotency-Key": idem.forPayload(body) },
        body,
      });
      setResult(record(data));
      idem.reset();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (result)
    return (
      <div className="space-y-3" data-testid="refund-result">
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold">
            {text(result, "status").toUpperCase() === "PENDING_APPROVAL"
              ? lang === "en"
                ? "Refund request submitted for administrative review"
                : "Demande de remboursement transmise pour examen administratif"
              : lang === "en"
                ? "Refund request submitted"
                : "Demande de remboursement transmise"}
          </p>
          <Tag value={text(result, "status")} />
        </div>
        <p className="text-xs text-muted-foreground">
          {lang === "en"
            ? "The refund will be processed according to the original payment route. A request awaiting review is not yet a completed refund."
            : "Le remboursement suit le circuit du paiement initial. Une demande en attente d’examen n’est pas encore un remboursement effectué."}
        </p>
        <KV data={result} />
        <OperationLookup
          compact
          initialKind="refunds"
          initialId={text(result, "reference", "id")}
        />
      </div>
    );

  return (
    <div className="space-y-3" data-testid="refund-form">
      <p className="text-sm font-bold">Remboursement</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1.5">
          <Label htmlFor="refund-amount">Montant ({tx.currency})</Label>
          <Input
            id="refund-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            aria-invalid={!value}
          />
          {value && Number(value) > tx.amount && (
            <p className="text-xs text-destructive">
              Supérieur au montant de la transaction.
            </p>
          )}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="refund-reason">Motif</Label>
          <Input
            id="refund-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Demande du client"
          />
        </div>
      </div>
      <ErrorBox error={error} onRetry={() => void submit()} />
      {confirming ? (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          <span className="flex-1">
            Confirmer le remboursement de{" "}
            <b>
              {value} {tx.currency}
            </b>{" "}
            sur {tx.reference} ?
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirming(false)}
            disabled={busy}
          >
            Annuler
          </Button>
          <Button
            size="sm"
            onClick={() => void submit()}
            disabled={busy}
            data-testid="button-refund-confirm"
          >
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}{" "}
            Confirmer
          </Button>
        </div>
      ) : (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onCancel}>
            Fermer
          </Button>
          <Button
            size="sm"
            disabled={!valid}
            onClick={() => setConfirming(true)}
            data-testid="button-refund-review"
          >
            Vérifier
          </Button>
        </div>
      )}
    </div>
  );
}
